import React, { useEffect, useRef, useState } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import { NavLink, useNavigate } from "react-router-dom";
import logo from "../assets/logo.webp";

const linkBase =
  "relative px-3 py-2 rounded-lg transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600";
const linkInactive = "text-gray-800 hover:text-green-900";
const linkActive = "text-white bg-green-900 shadow-md";

/** Sri Krishna flute (bansuri) — tiny decorative SVG for active state */
function FluteKrishna({ className = "h-3 w-16" }) {
  return (
    <svg
      viewBox="0 0 160 24"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="fluteGrad" x1="0" x2="1">
          <stop offset="0%" stopColor="#f2c66e" />
          <stop offset="100%" stopColor="#d9a74b" />
        </linearGradient>
      </defs>
      {/* body */}
      <rect x="8" y="10" width="130" height="4" rx="2" fill="url(#fluteGrad)" />
      {/* end cap */}
      <rect x="140" y="9" width="14" height="6" rx="2" fill="#c58e35" />
      {/* bindings */}
      <rect x="12" y="9" width="4" height="6" rx="1" fill="#8b5e1a" />
      <rect x="130" y="9" width="4" height="6" rx="1" fill="#8b5e1a" />
      {/* holes */}
      <g fill="#6b4e16">
        <circle cx="28" cy="12" r="2" />
        <circle cx="46" cy="12" r="2" />
        <circle cx="64" cy="12" r="2" />
        <circle cx="82" cy="12" r="2" />
        <circle cx="100" cy="12" r="2" />
        <circle cx="118" cy="12" r="2" />
      </g>
    </svg>
  );
}

function DesktopLink({ to, exact, children }) {
  return (
    <NavLink
      to={to}
      end={!!exact}
      className={({ isActive }) =>
        `${linkBase} ${isActive ? linkActive : linkInactive}`
      }
    >
      {({ isActive }) => (
        <span className="flex items-center gap-2">
          <span>{children}</span>
          {isActive && (
            <FluteKrishna className="h-3 w-16 animate-pulse drop-shadow-sm" />
          )}
        </span>
      )}
    </NavLink>
  );
}

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);

  // Courses dropdown (desktop) with anti-flicker + accessibility
  const [isCoursesOpen, setIsCoursesOpen] = useState(false);
  const coursesRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);

  const navigate = useNavigate();

  const openWithDelay = () => {
    clearTimeout(closeTimer.current);
    openTimer.current = setTimeout(() => setIsCoursesOpen(true), 80);
  };
  const closeWithDelay = () => {
    clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(() => setIsCoursesOpen(false), 120);
  };

  // outside click / Esc close
  useEffect(() => {
    const onDocClick = (e) => {
      if (!coursesRef.current?.contains(e.target)) setIsCoursesOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setIsCoursesOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const closeMenu = () => {
    setIsOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header className="bg-gray-50 w-full shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto flex justify-between items-center py-3 px-4">
        {/* Logo */}
        <div className="flex items-center justify-start">
          <div className="w-auto max-w-[240px] sm:max-w-[300px] md:max-w-[360px]">
            <img
              src={logo}
              alt="Rankridge Logo"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-2 text-sm font-medium">
          <DesktopLink to="/" exact>
            Home
          </DesktopLink>

          <DesktopLink to="/about-us">About</DesktopLink>

          {/* Courses (Desktop) */}
          <div
            ref={coursesRef}
            className="relative"
            onMouseEnter={openWithDelay}
            onMouseLeave={closeWithDelay}
            onFocusCapture={openWithDelay}
            onBlurCapture={closeWithDelay}
          >
            <button
              type="button"
              onClick={() => setIsCoursesOpen((v) => !v)}
              className={`${linkBase} ${linkInactive} flex items-center gap-1`}
              aria-haspopup="menu"
              aria-expanded={isCoursesOpen ? "true" : "false"}
              aria-controls="courses-menu"
            >
              Courses
              <svg
                className={`h-4 w-4 transition-transform ${
                  isCoursesOpen ? "rotate-180" : ""
                }`}
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
              </svg>
            </button>

            {isCoursesOpen && (
              <div
                id="courses-menu"
                role="menu"
                className="absolute left-0 top-full translate-y-2 w-80 bg-white border border-gray-200 shadow-xl rounded-xl p-2 z-[60]"
                onMouseEnter={openWithDelay}
                onMouseLeave={closeWithDelay}
              >
                {/* Each item shows flute when active */}
                <NavLink
                  to="/best-junior-college-in-hyderabad/"
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-lg transition ${
                      isActive
                        ? "bg-green-900 text-white"
                        : "text-gray-800 hover:bg-green-50 hover:text-green-900"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <span className="flex items-center justify-between gap-2">
                      <span>Intermediate With IIT-JEE & NEET</span>
                      {isActive && (
                        <FluteKrishna className="h-3 w-14 animate-pulse" />
                      )}
                    </span>
                  )}
                </NavLink>

                <NavLink
                  to="/mpc-with-iit-jee/"
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-lg transition ${
                      isActive
                        ? "bg-green-900 text-white"
                        : "text-gray-800 hover:bg-green-50 hover:text-green-900"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <span className="flex items-center justify-between gap-2">
                      <span>MPC With IIT-JEE</span>
                      {isActive && (
                        <FluteKrishna className="h-3 w-14 animate-pulse" />
                      )}
                    </span>
                  )}
                </NavLink>

                <NavLink
                  to="/best-neet-coaching-center-in-hyderbad/"
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-lg transition ${
                      isActive
                        ? "bg-green-900 text-white"
                        : "text-gray-800 hover:bg-green-50 hover:text-green-900"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <span className="flex items-center justify-between gap-2">
                      <span>BIPC With NEET</span>
                      {isActive && (
                        <FluteKrishna className="h-3 w-14 animate-pulse" />
                      )}
                    </span>
                  )}
                </NavLink>

                <NavLink
                  to="/engineering-medical-entrance-coaching-in-hyderabad/"
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-lg transition ${
                      isActive
                        ? "bg-green-900 text-white"
                        : "text-gray-800 hover:bg-green-50 hover:text-green-900"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <span className="flex items-center justify-between gap-2">
                      <span>IIT-JEE &amp; NEET Long Term</span>
                      {isActive && (
                        <FluteKrishna className="h-3 w-14 animate-pulse" />
                      )}
                    </span>
                  )}
                </NavLink>

                <NavLink
                  to="/neet-coaching-in-hyderabad/"
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-lg transition ${
                      isActive
                        ? "bg-green-900 text-white"
                        : "text-gray-800 hover:bg-green-50 hover:text-green-900"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <span className="flex items-center justify-between gap-2">
                      <span>NEET Long Term</span>
                      {isActive && (
                        <FluteKrishna className="h-3 w-14 animate-pulse" />
                      )}
                    </span>
                  )}
                </NavLink>

                <NavLink
                  to="/eamcet-coaching-in-hyderabad/"
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-lg transition ${
                      isActive
                        ? "bg-green-900 text-white"
                        : "text-gray-800 hover:bg-green-50 hover:text-green-900"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <span className="flex items-center justify-between gap-2">
                      <span>EAPCET Long Term</span>
                      {isActive && (
                        <FluteKrishna className="h-3 w-14 animate-pulse" />
                      )}
                    </span>
                  )}
                </NavLink>

                <NavLink
                  to="/iit-jee-neet-eamcet-bitsat-short-term-coaching-hyderabad/"
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-lg transition ${
                      isActive
                        ? "bg-green-900 text-white"
                        : "text-gray-800 hover:bg-green-50 hover:text-green-900"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <span className="flex items-center justify-between gap-2">
                      <span>Short-Term Courses</span>
                      {isActive && (
                        <FluteKrishna className="h-3 w-14 animate-pulse" />
                      )}
                    </span>
                  )}
                </NavLink>
              </div>
            )}
          </div>

          <DesktopLink to="/contact-us/">Contact</DesktopLink>
          <DesktopLink to="/track-records">Rankridge Track Record</DesktopLink>
          <DesktopLink to="/gellary">Gallery</DesktopLink>
          <DesktopLink to="/blog">Blog</DesktopLink>
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center">
          <button
            onClick={() => navigate("/signup/")}
            className="bg-green-900 text-white px-4 py-2 rounded-full font-medium hover:bg-green-700 shadow-md"
          >
            Register
          </button>
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden z-50 p-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
          onClick={() => setIsOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {isOpen ? (
            <FaTimes className="text-2xl text-black" />
          ) : (
            <FaBars className="text-3xl text-black" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white shadow-lg px-4 pt-3 pb-6 space-y-2 text-base font-medium">
          {/* helper to render mobile item with flute when active */}
          {[
            { to: "/", label: "Home", exact: true },
            { to: "/about-us", label: "About" },
          ].map(({ to, label, exact }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              onClick={() => setIsOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-2 rounded-lg ${
                  isActive ? "bg-green-900 text-white" : "text-gray-800 hover:bg-green-50 hover:text-green-900"
                }`
              }
            >
              {({ isActive }) => (
                <span className="flex items-center justify-between">
                  <span>{label}</span>
                  {isActive && <FluteKrishna className="h-3 w-14" />}
                </span>
              )}
            </NavLink>
          ))}

          {/* Mobile Courses (accordion) */}
          <details className="rounded-lg bg-gray-50 open:bg-gray-50">
            <summary className="cursor-pointer list-none px-3 py-2 rounded-lg text-gray-800 hover:text-green-900">
              Courses
            </summary>
            <div className="pl-3 space-y-1 mt-2 pb-2">
              {[
                { to: "/best-junior-college-in-hyderabad/", label: "Intermediate With IIT-JEE & NEET" },
                { to: "/mpc-with-iit-jee/", label: "MPC With IIT-JEE" },
                { to: "/best-neet-coaching-center-in-hyderbad/", label: "BIPC With NEET" },
                { to: "/engineering-medical-entrance-coaching-in-hyderabad/", label: "IIT-JEE & NEET Long Term" },
                { to: "/neet-coaching-in-hyderabad/", label: "NEET Long Term" },
                { to: "/eamcet-coaching-in-hyderabad/", label: "EAPCET Long Term" },
                { to: "/iit-jee-neet-eamcet-bitsat-short-term-coaching-hyderabad/", label: "Short-Term Courses" },
              ].map(({ to, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-2 rounded-lg ${
                      isActive ? "bg-green-900 text-white" : "text-gray-800 hover:bg-green-50 hover:text-green-900"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <span className="flex items-center justify-between">
                      <span>{label}</span>
                      {isActive && <FluteKrishna className="h-3 w-12" />}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </details>

          {[
            { to: "/contact-us/", label: "Contact" },
            { to: "/gellary", label: "Gallery" },
            { to: "/track-records", label: "Rankridge Track Record" },
            { to: "/blog", label: "Blog" },
          ].map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setIsOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-2 rounded-lg ${
                  isActive ? "bg-green-900 text-white" : "text-gray-800 hover:bg-green-50 hover:text-green-900"
                }`
              }
            >
              {({ isActive }) => (
                <span className="flex items-center justify-between">
                  <span>{label}</span>
                  {isActive && <FluteKrishna className="h-3 w-12" />}
                </span>
              )}
            </NavLink>
          ))}

          <hr className="my-3" />
          <button
            onClick={() => {
              setIsOpen(false);
              navigate("/signup/");
            }}
            className="w-full text-left bg-green-900 text-white px-4 py-2 rounded-full font-medium hover:bg-green-700 shadow"
          >
            Register
          </button>
        </div>
      )}
    </header>
  );
}
